/**
 * DeleteSecretModal Component
 *
 * Modal for confirming secret deletion.
 */

import React, { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { useDeleteSecret } from '../hooks/useDeleteSecret';
import type { SecretSanitized } from '../../../types/secret.types';

interface DeleteSecretModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  secret: SecretSanitized;
}

/**
 * DeleteSecretModal component
 */
export const DeleteSecretModal: React.FC<DeleteSecretModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  secret,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');

  const { mutate: deleteSecret, isPending } = useDeleteSecret(tenantId, {
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      setErrorMessage(error);
    },
  });

  const handleDelete = () => {
    setErrorMessage('');
    deleteSecret(secret.secretId);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage('');
      onClose();
    }
  };

  const hasAccessGrants = secret.accessGrants && secret.accessGrants.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Delete Secret</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Warning for access grants */}
          {hasAccessGrants && (
            <Alert variant="warning">
              This secret has {secret.accessGrants?.length} application(s) with access.
              Deleting it will remove access for all applications.
            </Alert>
          )}

          {/* Confirmation Message */}
          <p className="text-sm text-gray-600">
            Are you sure you want to delete the secret{' '}
            <span className="font-medium text-gray-900">{secret.secretName}</span>
            <span className="ml-1 font-mono text-xs text-gray-500">
              ({secret.secretCode})
            </span>
            ?
          </p>

          <p className="text-sm text-red-600 font-medium">
            This action cannot be undone.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={isPending}>
            Delete Secret
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
